/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete } from '@mui/material';
import { TextField, Chip, CircularProgress } from '@mui/material';

// Drop-in replacement for react-select built on MUI Autocomplete.
// Accepts react-select style props (options, value, onChange, isMulti, etc.)
// and option objects shaped { label, value, isFixed, isDisabled }.
const AutocompleteSelect = React.forwardRef((incomingProps, ref) => {
  const props = {
    ...{
      options: [],
      value: null,
      onChange: undefined,
      isMulti: false,
      isClearable: false,
      isSearchable: true,
      isDisabled: false,
      isLoading: false,
      placeholder: '',
      noOptionsMessage: undefined,
      name: undefined,
      className: undefined,
      popupIcon: undefined,
    },
    ...incomingProps,
  };
  const {
    options,
    value,
    onChange,
    isMulti,
    isClearable,
    isSearchable,
    isDisabled,
    isLoading,
    placeholder,
    noOptionsMessage,
    name,
    className,
    popupIcon,
  } = props;

  const handleChange = (event, newValue, reason, details) => {
    if (!onChange) return;
    // Mimic react-select's actionMeta so existing handlers keep working
    onChange(newValue, {
      action: reason,
      removedValue:
        reason === 'remove-option' ? details && details.option : undefined,
    });
  };

  const noOptionsText =
    typeof noOptionsMessage === 'function'
      ? noOptionsMessage()
      : noOptionsMessage;

  const renderValue = isMulti
    ? (selected, getItemProps) =>
        (selected || []).map((option, index) => {
          const itemProps = getItemProps({ index });
          return (
            <Chip
              {...itemProps}
              key={option.value ?? itemProps.key ?? index}
              label={option.label}
              size="small"
              onDelete={option.isFixed ? undefined : itemProps.onDelete}
            />
          );
        })
    : undefined;

  return (
    <Autocomplete
      multiple={isMulti}
      options={options}
      value={isMulti ? value || [] : value || null}
      onChange={handleChange}
      getOptionLabel={option => String((option && option.label) ?? '')}
      isOptionEqualToValue={(option, selected) =>
        String(option.value) === String(selected && selected.value)
      }
      getOptionDisabled={option => !!option.isDisabled}
      disabled={isDisabled}
      loading={isLoading}
      disableClearable={!isClearable}
      popupIcon={popupIcon}
      noOptionsText={noOptionsText}
      className={className}
      renderValue={renderValue}
      renderInput={params => {
        const { htmlInput, input: inputSlotProps } = params.slotProps ?? {};
        return (
          <TextField
            {...params}
            name={name}
            inputRef={ref}
            placeholder={placeholder}
            slotProps={{
              ...params.slotProps,
              htmlInput: {
                ...htmlInput,
                readOnly: isSearchable === false,
              },
              input: {
                ...inputSlotProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress size={16} /> : null}
                    {inputSlotProps?.endAdornment}
                  </>
                ),
              },
            }}
          />
        );
      }}
    />
  );
});

AutocompleteSelect.displayName = 'AutocompleteSelect';

AutocompleteSelect.propTypes = {
  options: PropTypes.array,
  value: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
  ]),
  onChange: PropTypes.func,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  noOptionsMessage: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  name: PropTypes.string,
  className: PropTypes.string,
  popupIcon: PropTypes.node,
};

export default AutocompleteSelect;
